import { DELETION_FLAG, WATCHTOWER_TYPE, type Watchtower, WatchtowerKey } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { type ReactNode, useMemo, useRef } from "react";
import { LuFlaskConical } from "react-icons/lu";
import { useAsyncFn } from "react-use";
import { type Merge } from "type-fest";
import {
  modifyWatchtower,
  updateWatchtowerCache,
  useTranslate,
  useWatchtower,
  verifyWatchtower,
} from "../../services";
import { css, cx } from "../../styled-system/css";
import { token } from "../../styled-system/tokens";
import {
  ButtonStyle,
  Spinner,
  SwitchStyle,
  TableStyle,
  TypographyStyle,
  skeletonStyle,
} from "../common/index";
import { showError } from "../utilities";

const classes = TableStyle.raw({ foot: false });

export type WatchtowerTableProperties = Merge<
  React.ComponentPropsWithoutRef<"div">,
  {
    action?: { (watchtower: Watchtower): ReactNode };
    columns?: Readonly<
      Partial<
        Record<"createdBy" | "deleted" | "name" | "type" | "verify" | "watchtowerKey", boolean>
      >
    >;
    loading?: boolean;
    rows?: ReadonlyArray<Watchtower>;
  }
>;

export function WatchtowerTable({
  action,
  className,
  columns,
  loading,
  rows = [],
  ...properties
}: WatchtowerTableProperties) {
  const translate = useTranslate();

  const parentRef = useRef<HTMLDivElement>(null);

  const gridTemplateColumns = useMemo(
    function () {
      return [
        columns?.watchtowerKey !== false ? "216px" : "",
        columns?.type !== false ? "168px" : "",
        columns?.name !== false ? "minmax(120px, 1fr)" : "",
        columns?.createdBy !== false ? "120px" : "",
        columns?.deleted !== false ? "48px" : "",
        columns?.verify !== false ? "104px" : "",
      ]
        .filter(function (column) {
          return column !== "";
        })
        .join(" ");
    },
    [columns],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 20,
  });

  const headCellClass = TypographyStyle({ color: "primary/60", ellipsis: true, fontWeight: 500 });

  return (
    <div
      {...properties}
      className={cx(
        css(classes.root, TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 })),
        className,
      )}
    >
      <div className={css(classes.head)}>
        <div className={css(classes.headRow)} style={{ gridTemplateColumns }}>
          {columns?.watchtowerKey !== false && (
            <div className={headCellClass}>{translate("%WatchtowerKey.0%")}</div>
          )}
          {columns?.type !== false && (
            <div className={headCellClass}>{translate("%WatchtowerType.0%")}</div>
          )}
          {columns?.name !== false && <div className={headCellClass}>{translate("%Name.0%")}</div>}
          {columns?.createdBy !== false && (
            <div className={headCellClass}>{translate("%Creator.0%")}</div>
          )}
          {columns?.deleted !== false && (
            <div className={headCellClass}>{translate("%Notification.0%")}</div>
          )}
          {columns?.verify !== false && (
            <div className={headCellClass}>{translate("%Verification.0%")}</div>
          )}
        </div>
      </div>

      <div className={css(classes.virtualOuterBody)} ref={parentRef}>
        <div
          className={css(classes.virtualInnerBody)}
          style={{ height: virtualizer.getTotalSize() + "px" }}
        >
          {virtualizer.getVirtualItems().map(function (virtualItem) {
            const watchtower = rows[virtualItem.index];
            return (
              <div
                className={css(classes.virtualBodyRow)}
                data-index={virtualItem.index}
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                style={{
                  gridTemplateColumns,
                  height: virtualItem.size + "px",
                  transform: "translateY(" + virtualItem.start + "px)",
                }}
              >
                <div
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "14px",
                    lineHeight: 1,
                  })}
                >
                  {watchtower.watchtowerKey}
                </div>
                <div
                  className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
                >
                  {translate("%WatchtowerType." + watchtower.type + ".0%")}
                </div>
                <div
                  className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
                >
                  {watchtower.name}
                  {watchtower.type === WATCHTOWER_TYPE.EMAIL_ADDRESS &&
                    " (" + watchtower.emailAddress + ")"}
                </div>
                <div
                  className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
                >
                  {watchtower.createdBy}
                </div>
                <DeletedCell watchtowerKey={watchtower.watchtowerKey} />
                <VerifyCell watchtowerKey={watchtower.watchtowerKey} />
              </div>
            );
          })}
        </div>

        {rows.length === 0 &&
          (loading ? (
            Array.from({ length: 5 }).map(function (_, index) {
              return (
                <div
                  className={css(classes.bodyRow, { padding: "16px" })}
                  key={index}
                  style={{ gridTemplateColumns }}
                >
                  <div className={css(skeletonStyle, { width: "stretch" })} />
                  <div className={css(skeletonStyle, { width: "stretch" })} />
                </div>
              );
            })
          ) : (
            <div className={css(classes.bodyRow, { padding: "16px" })}>
              <p className={css({ fontWeight: 500 })}>{translate("%NoItems.0%")}</p>
            </div>
          ))}
      </div>
    </div>
  );
}

function DeletedCell({ watchtowerKey }: { watchtowerKey: WatchtowerKey }) {
  const watchtower = useWatchtower(watchtowerKey, { virtualized: true });

  return (
    <input
      checked={watchtower?.deleted === DELETION_FLAG.DEFAULT}
      className={SwitchStyle({ display: "block" })}
      onChange={async function (event) {
        try {
          // 楽観的更新擬きを行う。
          updateWatchtowerCache(watchtowerKey, function (watchtower) {
            if (watchtower) {
              return {
                ...watchtower,
                deleted: event.target.checked ? DELETION_FLAG.DEFAULT : DELETION_FLAG.ARCHIVED,
              };
            }
          });

          await modifyWatchtower({
            deleted: event.target.checked ? DELETION_FLAG.DEFAULT : DELETION_FLAG.ARCHIVED,
            watchtowerKey,
          });
        } catch (error) {
          showError(error);
        } finally {
          updateWatchtowerCache(watchtowerKey, undefined, { invalidate: true });
        }
      }}
      type="checkbox"
    />
  );
}

function VerifyCell({ watchtowerKey }: { watchtowerKey: WatchtowerKey }) {
  const translate = useTranslate();
  const [{ loading }, verify] = useAsyncFn(
    async function () {
      try {
        await verifyWatchtower({ watchtowerKey });
      } catch (error) {
        showError(error);
      }
    },
    [watchtowerKey],
  );

  return (
    <div>
      <button
        className={ButtonStyle({
          appearance: "outlined",
          borderRadius: "4px",
          color: "outlined",
          ellipsis: true,
          flexGrow: 1,
          fontSize: "14px",
          gap: "8px",
          padding: "12px",
        })}
        onClick={function () {
          verify();
        }}
      >
        {loading ? (
          <Spinner color={token("colors.foreground")} size="16px" type="a" />
        ) : (
          <LuFlaskConical
            className={css({
              color: token("colors.foreground"),
              fontSize: "16px",
            })}
          />
        )}
        <span>{translate("%Test.0%")}</span>
      </button>
    </div>
  );
}
